/**
 * InfluentStudyPage.tsx — Influent Water Treatability Study
 *
 * Provides deep physicochemical, biological and engineering analysis of
 * influent water samples stored for the active customer session.
 * Analysis includes industry origin identification, BOD/COD biodegradability,
 * nutrient balance, toxicity screening, alkalinity buffering, equipment
 * suitability matrix and a full recommended treatment train.
 *
 * Reports can be saved to the database and retrieved from CustomerSitePage.
 */

import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, ReferenceLine,
} from 'recharts';
import {
  Droplets, FlaskConical, Microscope, Activity, AlertTriangle,
  CheckCircle2, ChevronRight, Download, Save, Beaker, Layers,
  Zap, Info, TrendingUp, TrendingDown, Minus, Building2,
  BarChart3, Dna, Shield, ClipboardList, ChevronDown, ChevronUp,
  RefreshCw, FileText, Printer, ArrowRight, Circle,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useSession } from '@/context/SessionContext';
import { toast } from 'sonner';
import {
  getAllLocalSamples,
} from '@/lib/hybridStorage';
import {
  apiListInfluentSamples, apiSaveReport, hasPhpBackend,
} from '@/lib/phpApi';
import {
  runTreatabilityAnalysis,
  type TreatabilityReport,
  type EquipmentSuitability,
  type TreatmentTrainStep,
  type ToxicityItem,
} from '@/lib/treatabilityAnalysis';
import type { WaterParameters } from '@/types/calculator';

// ─── Types ─────────────────────────────────────────────────────────────────

interface SampleRow {
  id: number;
  sampleName: string;
  reportNumber?: string | null;
  params: Record<string, number>;
  createdAt: string;
  sampleType?: string | null;
  source: 'local' | 'server';
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const PRIORITY_COLOR: Record<EquipmentSuitability['priority'], string> = {
  essential:       'text-red-400 bg-red-500/10 border-red-800/50',
  recommended:     'text-emerald-400 bg-emerald-500/10 border-emerald-800/50',
  optional:        'text-amber-400 bg-amber-500/10 border-amber-800/50',
  not_applicable:  'text-slate-500 bg-slate-800/40 border-slate-700/30',
};

const STAGE_COLOR: Record<TreatmentTrainStep['stage'], string> = {
  'pre-treatment': '#38bdf8',
  'primary':       '#a78bfa',
  'secondary':     '#10b981',
  'tertiary':      '#f59e0b',
  'polishing':     '#6366f1',
};

const PERFORMANCE_COLOR: Record<string, string> = {
  excellent: '#10b981', good: '#84cc16', moderate: '#f59e0b',
  challenging: '#f97316', complex: '#ef4444',
};

function riskBadge(level: ToxicityItem['riskLevel']) {
  const map: Record<typeof level, string> = {
    none: 'bg-slate-700 text-slate-400',
    low: 'bg-yellow-900/40 text-yellow-400',
    moderate: 'bg-orange-900/40 text-orange-400',
    high: 'bg-red-900/40 text-red-400',
    critical: 'bg-red-600 text-white',
  };
  return map[level];
}

function paramsToWater(p: Record<string, number>): WaterParameters {
  return {
    bod5: p.bod5 ?? p.bod ?? 0,
    cod: p.cod ?? 0,
    tss: p.tss ?? 0,
    turbidity: p.turbidity ?? 0,
    temperature: p.temperature ?? 20,
    color: p.color ?? 0,
    oilGrease: p.oilGrease ?? p.og ?? p.oandg ?? 0,
    freeOil: p.freeOil ?? 0,
    ph: p.ph ?? 7,
    tds: p.tds ?? 0,
    alkalinity: p.alkalinity ?? 0,
    chlorides: p.chlorides ?? p.chloride ?? 0,
    ammoniaN: p.ammoniaN ?? p.nh3n ?? p.nh3 ?? 0,
    totalNitrogen: p.totalNitrogen ?? p.tn ?? 0,
    totalPhosphorus: p.totalPhosphorus ?? p.tp ?? 0,
    tkn: p.tkn ?? 0,
    lead: p.lead ?? 0,
    mercury: p.mercury ?? 0,
    cadmium: p.cadmium ?? 0,
    chromium: p.chromium ?? 0,
    arsenic: p.arsenic ?? 0,
    nickel: p.nickel ?? 0,
    zinc: p.zinc ?? 0,
    copper: p.copper ?? 0,
    sulfides: p.sulfides ?? p.sulfide ?? 0,
    phenol: p.phenol ?? 0,
    surfactants: p.surfactants ?? 0,
    flowRate: p.flowRate ?? p.flow ?? 0,
    conductivity: p.conductivity ?? 0,
  };
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function SectionCard({ title, icon: Icon, children, accent = '#3b82f6' }: {
  title: string; icon: React.ElementType; children: React.ReactNode; accent?: string;
}) {
  return (
    <div className="rb-card overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-800/50 bg-rb-elevated/50">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        </div>
        <h3 className="text-sm font-bold text-slate-200 tracking-tight">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function ScoreGauge({ score, label, color }: { score: number; label: string; color: string }) {
  const r = 44; const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={108} height={108} viewBox="0 0 108 108">
        <circle cx={54} cy={54} r={r} fill="none" stroke="rgba(100,116,139,0.15)" strokeWidth={8} />
        <circle cx={54} cy={54} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transformOrigin: '54px 54px', transform: 'rotate(-90deg)', transition: 'stroke-dasharray 0.8s ease' }} />
        <text x={54} y={52} textAnchor="middle" fill="white" fontSize={18} fontWeight={700}>{score}</text>
        <text x={54} y={66} textAnchor="middle" fill="#94a3b8" fontSize={10}>/100</text>
      </svg>
      <span className="text-xs font-semibold" style={{ color }}>{label}</span>
    </div>
  );
}

function ParamPill({ label, value, unit, color = 'text-slate-300' }: { label: string; value: number; unit: string; color?: string }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-rb-elevated border border-slate-800/50">
      <span className="text-[11px] text-slate-500 font-medium">{label}</span>
      <span className={`text-sm font-bold font-mono ${color}`}>{value > 0 ? value.toFixed(value < 10 ? 2 : 0) : '—'}{unit && <span className="text-[10px] text-slate-500 ml-0.5">{unit}</span>}</span>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function InfluentStudyPage() {
  const { currentCustomer } = useSession();
  const [samples, setSamples] = useState<SampleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'fingerprint' | 'physicochemical' | 'treatability' | 'design' | 'report'>('fingerprint');
  const [saving, setSaving] = useState(false);
  const [expandedEquip, setExpandedEquip] = useState<string | null>(null);

  // Load samples from local + server
  const loadSamples = useCallback(async () => {
    setLoading(true);
    try {
      const local = getAllLocalSamples()
        .filter(s => s.sampleType === 'influent' || !s.sampleType)
        .map((s): SampleRow => ({
          id: s.id, sampleName: s.sampleName,
          reportNumber: s.reportNumber, params: s.params,
          createdAt: s.createdAt, sampleType: s.sampleType, source: 'local',
        }));

      let server: SampleRow[] = [];
      if (hasPhpBackend()) {
        try {
          const result = await apiListInfluentSamples(currentCustomer?.serverId, { type: 'influent' });
          server = (result ?? []).map((s: Record<string, unknown>): SampleRow => ({
            id: s.id as number,
            sampleName: (s.sampleName ?? s.sample_name ?? 'Sample') as string,
            reportNumber: (s.reportNumber ?? s.report_number ?? null) as string | null,
            params: (s.params ?? {}) as Record<string, number>,
            createdAt: (s.createdAt ?? s.created_at ?? '') as string,
            sampleType: (s.sampleType ?? s.sample_type ?? 'influent') as string,
            source: 'server',
          }));
        } catch {
          // server unavailable — local only
        }
      }

      // Merge, deduplicate by id
      const seen = new Set<number>();
      const merged: SampleRow[] = [];
      for (const s of [...server, ...local]) {
        if (!seen.has(s.id)) { seen.add(s.id); merged.push(s); }
      }
      merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setSamples(merged);
      if (merged.length > 0 && !selectedId) setSelectedId(merged[0].id);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [currentCustomer, selectedId]);

  // Auto-load
  useMemo(() => { if (!loaded) loadSamples(); }, [loaded, loadSamples]);

  // Selected sample
  const selected = useMemo(() => samples.find(s => s.id === selectedId), [samples, selectedId]);

  // Run analysis
  const report = useMemo((): TreatabilityReport | null => {
    if (!selected) return null;
    const wp = paramsToWater(selected.params);
    const date = new Date(selected.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    return runTreatabilityAnalysis(wp, selected.sampleName, date);
  }, [selected]);

  // Radar chart data for water fingerprint
  const radarData = useMemo(() => {
    if (!selected) return [];
    const p = selected.params;
    const norm = (val: number, max: number) => Math.min(100, (val / max) * 100);
    return [
      { subject: 'BOD₅', A: norm(p.bod5 ?? p.bod ?? 0, 1000) },
      { subject: 'COD',  A: norm(p.cod ?? 0, 2000) },
      { subject: 'TSS',  A: norm(p.tss ?? 0, 800) },
      { subject: 'O&G',  A: norm(p.oilGrease ?? p.og ?? 0, 200) },
      { subject: 'NH₃-N',A: norm(p.ammoniaN ?? p.nh3n ?? 0, 200) },
      { subject: 'TP',   A: norm(p.totalPhosphorus ?? p.tp ?? 0, 50) },
      { subject: 'TDS',  A: norm(p.tds ?? 0, 10000) },
    ];
  }, [selected]);

  // Equipment bar chart
  const equipChart = useMemo(() => {
    if (!report) return [];
    return report.equipmentSuitability
      .filter(e => e.score > 20)
      .slice(0, 10)
      .map(e => ({ name: e.name.split(' ')[0], score: e.score, priority: e.priority, color: e.score >= 75 ? '#10b981' : e.score >= 50 ? '#f59e0b' : '#94a3b8' }));
  }, [report]);

  // Save report
  const handleSaveReport = async () => {
    if (!report || !selected) return;
    setSaving(true);
    try {
      if (hasPhpBackend()) {
        await apiSaveReport({
          reportType: 'influent_study',
          customerId: currentCustomer?.serverId ?? null,
          sampleId: selected.id,
          sampleName: selected.sampleName,
          reportData: report,
          generatedAt: new Date().toISOString(),
          title: `Influent Treatability Study — ${selected.sampleName}`,
        });
        toast.success('Study report saved to database');
      } else {
        // Local save
        const key = `rb_influent_study_${selected.id}`;
        localStorage.setItem(key, JSON.stringify({ report, savedAt: new Date().toISOString() }));
        toast.success('Study report saved locally');
      }
    } catch {
      toast.error('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'fingerprint',     label: 'Water Fingerprint', short: 'Fingerprint',  icon: Dna,           color: '#a855f7' },
    { id: 'physicochemical', label: 'Physicochemical',   short: 'Physico.',     icon: FlaskConical,  color: '#06b6d4' },
    { id: 'treatability',    label: 'Treatability',       short: 'Treatability', icon: Microscope,    color: '#10b981' },
    { id: 'design',          label: 'Treatment Design',   short: 'Design',       icon: Layers,        color: '#f59e0b' },
    { id: 'report',          label: 'Study Report',       short: 'Report',       icon: FileText,      color: '#3b82f6' },
  ] as const;

  return (
    <div className="space-y-6 py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        icon={Droplets}
        title="Influent Treatability Study"
        subtitle={currentCustomer ? `${currentCustomer.name} — ${currentCustomer.company || 'Site Analysis'}` : 'Select a customer to link study to site'}
        iconColor="text-blue-400"
        badge={report && (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold border"
            style={{ color: report.treatabilityColor, borderColor: `${report.treatabilityColor}40`, background: `${report.treatabilityColor}15` }}>
            {report.treatabilityRating.charAt(0).toUpperCase() + report.treatabilityRating.slice(1)} Treatability
          </span>
        )}
        actions={
          <div className="flex gap-2">
            <button onClick={loadSamples} disabled={loading}
              className="px-3 py-1.5 text-xs font-bold text-slate-400 hover:text-slate-200 bg-rb-elevated hover:bg-rb-elevated/80 border border-slate-800/60 rounded-lg transition-colors flex items-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {report && (
              <button onClick={handleSaveReport} disabled={saving}
                className="px-3 py-1.5 text-xs font-bold text-black bg-rb-brand hover:bg-yellow-400 rounded-lg transition-colors flex items-center gap-1.5">
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Saving…' : 'Save Report'}
              </button>
            )}
          </div>
        }
      />

      {/* Sample Selector */}
      <div className="rb-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-blue-400" />
            Select Influent Sample to Analyse
          </h3>
          <span className="text-[11px] text-slate-500">{samples.length} sample{samples.length !== 1 ? 's' : ''} available</span>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-4 justify-center">
            <RefreshCw className="w-4 h-4 animate-spin" /> Loading samples…
          </div>
        )}

        {!loading && samples.length === 0 && (
          <div className="text-center py-6 text-slate-500 text-sm">
            <Droplets className="w-8 h-8 mx-auto mb-2 opacity-30" />
            No influent samples found. Add samples via <strong className="text-slate-400">Influent Entry</strong> or <strong className="text-slate-400">Site Assessment</strong>.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {samples.map(s => {
            const isSelected = s.id === selectedId;
            const date = new Date(s.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
            return (
              <button key={s.id} onClick={() => { setSelectedId(s.id); setActiveTab('fingerprint'); }}
                className={`text-left p-3 rounded-xl border-2 transition-all ${isSelected
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-slate-800/60 bg-rb-elevated hover:border-slate-700'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-slate-200 truncate">{s.sampleName}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{date}</div>
                  </div>
                  <div className="flex flex-col gap-1 items-end shrink-0">
                    {s.source === 'local' && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">LOCAL</span>
                    )}
                    {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-blue-400" />}
                  </div>
                </div>
                {/* Mini param preview */}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {['bod5', 'cod', 'tss'].map(k => {
                    const v = s.params[k] ?? s.params[k === 'bod5' ? 'bod' : k];
                    return v > 0 ? (
                      <span key={k} className="text-[10px] font-mono text-slate-400">
                        {k.toUpperCase()} <span className="text-slate-300 font-bold">{Math.round(v)}</span>
                      </span>
                    ) : null;
                  })}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Analysis Panel */}
      <AnimatePresence mode="wait">
        {report && (
          <motion.div key={selectedId} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {/* Warnings banner */}
            {report.warnings.length > 0 && (
              <div className="mb-4 p-3 rounded-xl border border-red-800/60 bg-red-500/5 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  {report.warnings.map((w, i) => <p key={i} className="text-xs text-red-300 font-medium">{w}</p>)}
                </div>
              </div>
            )}

            {/* KPI Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Treatability Score', value: `${report.overallTreatabilityScore}/100`, color: report.treatabilityColor, desc: report.treatabilityRating },
                { label: 'BOD:COD Ratio', value: report.biodegradability.bodCodRatio.toFixed(2), color: report.biodegradability.color, desc: report.biodegradability.label },
                { label: 'Origin Confidence', value: `${report.industryOrigin.confidencePct}%`, color: '#a78bfa', desc: report.industryOrigin.primary },
                { label: 'Toxic Compounds', value: String(report.toxicityScreen.length), color: report.toxicityScreen.length > 0 ? '#f97316' : '#10b981', desc: report.toxicityScreen.length > 0 ? 'Inhibitors detected' : 'None detected' },
              ].map((kpi, i) => (
                <div key={i} className="rb-card p-3 text-center">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{kpi.label}</div>
                  <div className="text-2xl font-bold mt-1" style={{ color: kpi.color }}>{kpi.value}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 truncate">{kpi.desc}</div>
                </div>
              ))}
            </div>

            {/* Tab Navigation — coloured pills with shadow, slow pulse on active */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    title={tab.label}
                    className="relative flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 rounded-xl text-[11px] sm:text-xs font-bold uppercase tracking-wide transition-all border-2 overflow-hidden"
                    style={{
                      background: active
                        ? `linear-gradient(135deg, ${tab.color}38, ${tab.color}12)`
                        : `linear-gradient(135deg, ${tab.color}14, ${tab.color}06)`,
                      borderColor: active ? `${tab.color}` : `${tab.color}55`,
                      color: active ? '#f8fafc' : tab.color,
                      boxShadow: active
                        ? `0 4px 14px ${tab.color}55, 0 0 0 1px ${tab.color}88 inset, 0 0 22px ${tab.color}44`
                        : `0 2px 6px ${tab.color}25, 0 0 0 1px ${tab.color}33 inset`,
                    }}
                  >
                    {active && (
                      <span
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{
                          boxShadow: `0 0 0 0 ${tab.color}80`,
                          animation: 'rb-tab-pulse 2.6s ease-in-out infinite',
                        }}
                      />
                    )}
                    <Icon className="w-4 h-4 shrink-0 drop-shadow" style={{ filter: active ? `drop-shadow(0 0 4px ${tab.color}cc)` : 'none' }} />
                    <span className="leading-tight text-center sm:text-left">{tab.short}</span>
                  </button>
                );
              })}
            </div>
            <style>{`
              @keyframes rb-tab-pulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.0); opacity: 0.85; }
                50%      { box-shadow: 0 0 0 6px rgba(255,255,255,0.0); opacity: 1; }
              }
            `}</style>

            {/* Tab: Water Fingerprint */}
            {activeTab === 'fingerprint' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Industry Origin */}
                <SectionCard title="Industry Origin Classification" icon={Building2} accent="#a78bfa">
                  <div className="flex items-start gap-4">
                    <div className="text-center shrink-0">
                      <div className="w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-800/40 flex items-center justify-center mx-auto">
                        <Building2 className="w-8 h-8 text-purple-400" />
                      </div>
                      <div className={`mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        report.industryOrigin.confidence === 'high' ? 'bg-emerald-500/15 text-emerald-400' :
                        report.industryOrigin.confidence === 'medium' ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-700 text-slate-400'
                      }`}>
                        {report.industryOrigin.confidence.toUpperCase()} CONFIDENCE
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-lg font-bold text-slate-100">{report.industryOrigin.primary}</div>
                      {report.industryOrigin.secondary && (
                        <div className="text-sm text-slate-400 mt-0.5">Secondary: {report.industryOrigin.secondary}</div>
                      )}
                      <p className="text-xs text-slate-500 mt-2 leading-relaxed">{report.industryOrigin.characteristics}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {report.industryOrigin.indicators.map((ind, i) => (
                          <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700/50">{ind}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Score bar */}
                  <div className="mt-4 pt-4 border-t border-slate-800/50">
                    <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                      <span>Classification strength</span>
                      <span>{report.industryOrigin.confidencePct}%</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-purple-600 to-blue-500 transition-all"
                        style={{ width: `${report.industryOrigin.confidencePct}%` }} />
                    </div>
                  </div>
                </SectionCard>

                {/* Radar Chart */}
                <SectionCard title="Parameter Signature Profile" icon={BarChart3} accent="#3b82f6">
                  {radarData.length >= 3 ? (
                    <>
                      <ResponsiveContainer width="100%" height={260}>
                        <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                          <PolarGrid stroke="rgba(100,116,139,0.15)" />
                          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                          <Radar name="Sample" dataKey="A" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.18} strokeWidth={2} />
                          <Tooltip formatter={(v: number) => [`${Math.round(v)}% of range`, '']} />
                        </RadarChart>
                      </ResponsiveContainer>
                      <p className="text-[10px] text-slate-500 text-center">Values normalised to typical industrial wastewater ranges</p>
                    </>
                  ) : (
                    <div className="h-[260px] flex flex-col items-center justify-center gap-2 text-slate-600">
                      <BarChart3 className="w-8 h-8 opacity-20" />
                      <p className="text-xs">Select a sample to generate the fingerprint radar</p>
                    </div>
                  )}
                </SectionCard>

                {/* Biodegradability */}
                <SectionCard title="Biodegradability Assessment" icon={Dna} accent={report.biodegradability.color}>
                  <div className="flex items-start gap-4">
                    <ScoreGauge
                      score={report.biodegradability.biologicalTreatmentSuitability}
                      label="Bio Suitability"
                      color={report.biodegradability.color}
                    />
                    <div className="flex-1">
                      <div className="text-base font-bold mb-1" style={{ color: report.biodegradability.color }}>
                        {report.biodegradability.label}
                      </div>
                      <div className="text-[11px] text-slate-400 space-y-1">
                        <div>BOD:COD = <span className="font-mono font-bold text-slate-200">{report.biodegradability.bodCodRatio.toFixed(3)}</span></div>
                        <div>Expected BOD removal: <span className="font-bold text-slate-200">{report.biodegradability.expectedBODRemoval}%</span></div>
                      </div>
                      <p className="text-xs text-slate-500 mt-2 leading-relaxed">{report.biodegradability.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 rounded-xl bg-rb-elevated border border-slate-800/50">
                    <p className="text-xs text-slate-400 leading-relaxed">
                      <span className="font-bold text-slate-300">Recommendation: </span>{report.biodegradability.recommendation}
                    </p>
                  </div>
                </SectionCard>

                {/* Key Findings */}
                <SectionCard title="Key Findings" icon={Info} accent="#10b981">
                  <div className="space-y-2">
                    {report.keyFindings.map((f, i) => (
                      <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-rb-elevated border border-slate-800/40">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                        <span className="text-xs text-slate-400">{f}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            )}

            {/* Tab: Physicochemical */}
            {activeTab === 'physicochemical' && selected && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Organics */}
                <SectionCard title="Organic Contaminants" icon={Activity} accent="#f97316">
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['BOD₅', 'bod5', 'bod', 'mg/L'],
                      ['COD', 'cod', null, 'mg/L'],
                      ['O&G', 'oilGrease', 'og', 'mg/L'],
                      ['Phenol', 'phenol', null, 'mg/L'],
                      ['Surfactants', 'surfactants', null, 'mg/L'],
                      ['Color', 'color', null, 'Pt-Co'],
                    ].map(([label, k1, k2, unit]) => {
                      const v = selected.params[k1 as string] ?? (k2 ? selected.params[k2 as string] : 0) ?? 0;
                      const color = v > 0 ? 'text-orange-300' : 'text-slate-600';
                      return <ParamPill key={label as string} label={label as string} value={v} unit={unit as string} color={color} />;
                    })}
                  </div>
                </SectionCard>

                {/* Solids & Physical */}
                <SectionCard title="Solids & Physical Parameters" icon={Layers} accent="#38bdf8">
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['TSS', 'tss', null, 'mg/L'],
                      ['Turbidity', 'turbidity', null, 'NTU'],
                      ['TDS', 'tds', null, 'mg/L'],
                      ['Conductivity', 'conductivity', null, 'µS/cm'],
                      ['Temperature', 'temperature', null, '°C'],
                      ['pH', 'ph', null, ''],
                    ].map(([label, k1, , unit]) => {
                      const v = selected.params[k1 as string] ?? 0;
                      const color = label === 'pH' ? (v < 6 || v > 9 ? 'text-red-400' : 'text-emerald-400') : 'text-sky-300';
                      return <ParamPill key={label as string} label={label as string} value={v} unit={unit as string} color={color} />;
                    })}
                  </div>
                </SectionCard>

                {/* Nutrients */}
                <SectionCard title="Nutrients & Chemistry" icon={Zap} accent="#a78bfa">
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['NH₃-N', 'ammoniaN', 'nh3n', 'mg/L'],
                      ['Total N', 'totalNitrogen', 'tn', 'mg/L'],
                      ['Total P', 'totalPhosphorus', 'tp', 'mg/L'],
                      ['TKN', 'tkn', null, 'mg/L'],
                      ['Alkalinity', 'alkalinity', null, 'mg/L'],
                      ['Chlorides', 'chlorides', 'chloride', 'mg/L'],
                    ].map(([label, k1, k2, unit]) => {
                      const v = selected.params[k1 as string] ?? (k2 ? selected.params[k2 as string] : 0) ?? 0;
                      return <ParamPill key={label as string} label={label as string} value={v} unit={unit as string} color="text-purple-300" />;
                    })}
                  </div>

                  {/* Nutrient balance */}
                  <div className="mt-4 pt-4 border-t border-slate-800/50 space-y-2">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Nutrient Balance (BOD:N:P)</div>
                    {(['nStatus', 'pStatus'] as const).map(key => {
                      const nb = report.nutrientBalance;
                      const status = nb[key];
                      const isN = key === 'nStatus';
                      const ratio = isN ? nb.bodNRatio : nb.bodPRatio;
                      const ideal = isN ? 20 : 100;
                      const label = isN ? 'Nitrogen' : 'Phosphorus';
                      const color = status === 'adequate' ? 'text-emerald-400' : status === 'deficient' ? 'text-amber-400' : 'text-blue-400';
                      return (
                        <div key={key} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-rb-elevated border border-slate-800/40">
                          <span className="text-xs text-slate-400">{label}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono text-slate-500">BOD:{isN ? 'N' : 'P'} = {ratio?.toFixed(1) ?? '—'} (ideal {ideal})</span>
                            <span className={`text-[10px] font-bold capitalize ${color}`}>{status}</span>
                          </div>
                        </div>
                      );
                    })}
                    {report.nutrientBalance.notes.map((n, i) => (
                      <p key={i} className="text-[10px] text-slate-500 pl-1">{n}</p>
                    ))}
                  </div>
                </SectionCard>

                {/* Heavy Metals */}
                <SectionCard title="Heavy Metals Panel" icon={Shield} accent="#ef4444">
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['Lead (Pb)', 'lead'], ['Mercury (Hg)', 'mercury'], ['Cadmium (Cd)', 'cadmium'],
                      ['Chromium (Cr)', 'chromium'], ['Arsenic (As)', 'arsenic'], ['Nickel (Ni)', 'nickel'],
                      ['Zinc (Zn)', 'zinc'], ['Copper (Cu)', 'copper'],
                    ].map(([label, key]) => {
                      const v = selected.params[key as string] ?? 0;
                      const color = v > 0.1 ? 'text-red-400' : v > 0 ? 'text-amber-400' : 'text-slate-600';
                      return <ParamPill key={label as string} label={label as string} value={v} unit="mg/L" color={color} />;
                    })}
                  </div>
                </SectionCard>
              </div>
            )}

            {/* Tab: Treatability */}
            {activeTab === 'treatability' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Overall Score */}
                <SectionCard title="Overall Treatability Rating" icon={Activity} accent={report.treatabilityColor}>
                  <div className="flex items-center gap-6">
                    <ScoreGauge score={report.overallTreatabilityScore} label={report.treatabilityRating} color={report.treatabilityColor} />
                    <div className="flex-1 space-y-2">
                      {[
                        { label: 'Biodegradability', score: report.biodegradability.biologicalTreatmentSuitability, color: report.biodegradability.color },
                        { label: 'Toxicity Impact', score: Math.max(0, 100 - report.toxicityScreen.length * 20), color: report.toxicityScreen.length === 0 ? '#10b981' : '#f97316' },
                        { label: 'pH Suitability', score: Math.max(0, 100 - Math.abs(report.biodegradability.bodCodRatio * 0 + (selected?.params.ph ?? 7) - 7) * 15), color: '#3b82f6' },
                        { label: 'Alkalinity Buffer', score: report.buffering.adequateBuffering ? 90 : 40, color: report.buffering.adequateBuffering ? '#10b981' : '#f59e0b' },
                      ].map(item => (
                        <div key={item.label}>
                          <div className="flex justify-between text-[10px] mb-0.5">
                            <span className="text-slate-500">{item.label}</span>
                            <span className="font-bold" style={{ color: item.color }}>{Math.round(item.score)}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${item.score}%`, background: item.color }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </SectionCard>

                {/* Alkalinity / Buffering */}
                <SectionCard title="Alkalinity & Buffering Capacity" icon={Beaker} accent="#06b6d4">
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Alkalinity', value: `${report.buffering.alkalinity} mg/L`, ok: report.buffering.adequateBuffering },
                        { label: 'Min Required', value: `${report.buffering.recommendedMinAlkalinity} mg/L`, ok: null },
                        { label: 'Nitrification Risk', value: report.buffering.nitrificationRisk, ok: report.buffering.nitrificationRisk === 'low' },
                      ].map(item => (
                        <div key={item.label} className="p-2 rounded-lg bg-rb-elevated border border-slate-800/40 text-center">
                          <div className="text-[10px] text-slate-500">{item.label}</div>
                          <div className={`text-sm font-bold mt-0.5 ${item.ok === null ? 'text-slate-300' : item.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                    {report.buffering.notes.map((n, i) => (
                      <div key={i} className="flex gap-2 items-start p-2.5 rounded-lg bg-amber-500/5 border border-amber-800/30">
                        <Info className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-300/80">{n}</p>
                      </div>
                    ))}
                    {report.buffering.supplementRequired && (
                      <div className="p-3 rounded-xl bg-rb-elevated border border-cyan-800/30">
                        <div className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider mb-1">Supplement Required</div>
                        <div className="text-xs text-slate-300">{report.buffering.supplement}</div>
                        <div className="text-xs text-slate-400 mt-1 font-mono">{report.buffering.supplementDose}</div>
                      </div>
                    )}
                  </div>
                </SectionCard>

                {/* Toxicity Screen */}
                <div className="lg:col-span-2">
                  <SectionCard title="Biological Toxicity & Inhibitor Screening" icon={AlertTriangle} accent="#ef4444">
                    {report.toxicityScreen.length === 0 ? (
                      <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/5 border border-emerald-800/30">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                        <div>
                          <div className="text-sm font-bold text-emerald-400">No significant inhibitors detected</div>
                          <div className="text-xs text-slate-500 mt-0.5">All screened compounds are below biological inhibition thresholds</div>
                        </div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-rb-elevated text-slate-500 text-[10px] uppercase tracking-wider">
                              <th className="text-left px-3 py-2 font-bold">Compound</th>
                              <th className="text-right px-3 py-2 font-bold">Value</th>
                              <th className="text-right px-3 py-2 font-bold">Inhibition Threshold</th>
                              <th className="text-right px-3 py-2 font-bold">% of Threshold</th>
                              <th className="text-center px-3 py-2 font-bold">Risk</th>
                              <th className="text-left px-3 py-2 font-bold">Biological Effect</th>
                              <th className="text-left px-3 py-2 font-bold">Mitigation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {report.toxicityScreen.map((t, i) => (
                              <tr key={i} className="border-t border-slate-800/40 hover:bg-rb-elevated/50">
                                <td className="px-3 py-2 font-semibold text-slate-300">{t.label}</td>
                                <td className="px-3 py-2 text-right font-mono">{t.value} {t.unit}</td>
                                <td className="px-3 py-2 text-right font-mono text-slate-500">{t.bioInhibitionThreshold} {t.unit}</td>
                                <td className="px-3 py-2 text-right">
                                  <span className={`font-bold font-mono ${t.riskLevel === 'critical' || t.riskLevel === 'high' ? 'text-red-400' : 'text-amber-400'}`}>
                                    {t.percentOfThreshold}%
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${riskBadge(t.riskLevel)}`}>{t.riskLabel}</span>
                                </td>
                                <td className="px-3 py-2 text-slate-500 max-w-[180px]">{t.biologicalEffect}</td>
                                <td className="px-3 py-2 text-slate-500 max-w-[200px]">{t.mitigation}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </SectionCard>
                </div>
              </div>
            )}

            {/* Tab: Treatment Design */}
            {activeTab === 'design' && (
              <div className="space-y-4">
                {/* Equipment Suitability Bar Chart */}
                <SectionCard title="Equipment Suitability Matrix" icon={BarChart3} accent="#f59e0b">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={equipChart} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" horizontal={false} />
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} width={60} />
                        <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }}
                          formatter={(v: number) => [`${v}/100`, 'Suitability']} />
                        <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                          {equipChart.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Bar>
                        <ReferenceLine x={70} stroke="#f59e0b" strokeDasharray="4 2" label={{ value: 'Recommended', fill: '#f59e0b', fontSize: 9, position: 'top' }} />
                      </BarChart>
                    </ResponsiveContainer>

                    <div className="space-y-2">
                      {report.equipmentSuitability.slice(0, 8).map(eq => (
                        <div key={eq.id}>
                          <button onClick={() => setExpandedEquip(expandedEquip === eq.id ? null : eq.id)}
                            className="w-full flex items-center gap-2 p-2.5 rounded-lg hover:bg-rb-elevated transition-colors text-left">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-rb-elevated border border-slate-800/40 shrink-0">
                              <span className="text-[10px] font-bold text-slate-300">{eq.score}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-200">{eq.name}</span>
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border capitalize ${PRIORITY_COLOR[eq.priority]}`}>{eq.priority}</span>
                              </div>
                              <div className="h-1 bg-slate-800 rounded-full mt-1.5">
                                <div className="h-full rounded-full" style={{ width: `${eq.score}%`, background: eq.score >= 75 ? '#10b981' : eq.score >= 50 ? '#f59e0b' : '#64748b' }} />
                              </div>
                            </div>
                            {expandedEquip === eq.id ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
                          </button>
                          {expandedEquip === eq.id && (
                            <div className="mx-2 mb-1 px-3 py-2 rounded-lg bg-rb-elevated border border-slate-800/40 text-xs text-slate-400 space-y-1">
                              <p>{eq.reason}</p>
                              <p className="font-mono text-[11px] text-slate-500 pt-1">{eq.designNote}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </SectionCard>

                {/* Treatment Train */}
                <SectionCard title="Recommended Treatment Train" icon={ArrowRight} accent="#10b981">
                  <div className="space-y-3">
                    {report.recommendedTrain.map((step, idx) => {
                      const stageColor = STAGE_COLOR[step.stage];
                      return (
                        <div key={idx} className="flex gap-3">
                          {/* Connector */}
                          <div className="flex flex-col items-center">
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold text-white shrink-0"
                              style={{ background: stageColor }}>
                              {step.order}
                            </div>
                            {idx < report.recommendedTrain.length - 1 && (
                              <div className="w-0.5 flex-1 mt-1" style={{ background: `${stageColor}40` }} />
                            )}
                          </div>

                          <div className="flex-1 pb-3">
                            <div className="rb-card p-3.5">
                              <div className="flex items-start justify-between gap-2 flex-wrap">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-bold text-slate-100">{step.equipment}</span>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full capitalize" style={{ color: stageColor, background: `${stageColor}15`, border: `1px solid ${stageColor}30` }}>
                                      {step.stage}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-400 mt-1">{step.purpose}</p>
                                </div>
                                <span className="text-[10px] font-mono text-slate-500 text-right shrink-0">{step.designParam}</span>
                              </div>

                              {/* Expected removals */}
                              {step.keyRemoval.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2.5 pt-2.5 border-t border-slate-800/40">
                                  <span className="text-[10px] text-slate-600 self-center">Expected removal:</span>
                                  {step.keyRemoval.map(r => (
                                    <span key={r.param} className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-900/30">
                                      {r.label} {r.expected}%
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Chem dosing */}
                              {step.chemDosing && step.chemDosing.length > 0 && (
                                <div className="mt-2.5 pt-2.5 border-t border-slate-800/40">
                                  <div className="text-[10px] font-bold text-amber-500 uppercase tracking-wider mb-1.5">Chemical Dosing</div>
                                  {step.chemDosing.map((c, ci) => (
                                    <div key={ci} className="flex items-center gap-2 text-[11px] text-slate-400">
                                      <Beaker className="w-3 h-3 text-amber-500 shrink-0" />
                                      <span className="font-bold text-amber-300">{c.chemical}</span>
                                      <span className="font-mono text-slate-500">{c.dose}</span>
                                      <span className="text-slate-600">— {c.purpose}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
              </div>
            )}

            {/* Tab: Report */}
            {activeTab === 'report' && (
              <div className="space-y-4">
                <div className="flex justify-end gap-2">
                  <button onClick={() => window.print()}
                    className="px-4 py-2 text-xs font-bold text-slate-300 bg-rb-elevated hover:bg-slate-700 border border-slate-800/60 rounded-xl transition-colors flex items-center gap-2">
                    <Printer className="w-3.5 h-3.5" /> Print Report
                  </button>
                  <button onClick={handleSaveReport} disabled={saving}
                    className="px-4 py-2 text-xs font-bold text-black bg-rb-brand hover:bg-yellow-400 rounded-xl transition-colors flex items-center gap-2">
                    <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save to Database'}
                  </button>
                </div>

                {/* Report card */}
                <div className="rb-card p-6 space-y-6 print:shadow-none">
                  {/* Report header */}
                  <div className="border-b border-slate-800/50 pb-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-slate-100">Influent Water Treatability Study</h2>
                        <p className="text-sm text-slate-500 mt-0.5">{selected?.sampleName} — {report.biodegradability.bodCodRatio ? `BOD:COD ${report.biodegradability.bodCodRatio.toFixed(2)}` : ''}</p>
                        {currentCustomer && (
                          <p className="text-sm text-slate-400 mt-1">{currentCustomer.name} / {currentCustomer.company}</p>
                        )}
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <div>Generated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                        <div>Reynolds & Bauhm</div>
                        <div className="font-bold mt-1 capitalize" style={{ color: report.treatabilityColor }}>{report.treatabilityRating} Treatability</div>
                      </div>
                    </div>
                  </div>

                  {/* Executive Summary */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-200 mb-3">Executive Summary</h3>
                    <div className="p-4 rounded-xl bg-rb-elevated border border-slate-800/50 text-xs text-slate-400 leading-relaxed space-y-2">
                      <p>This treatability study analyses sample <strong className="text-slate-200">{selected?.sampleName}</strong> and classifies the wastewater as originating from <strong className="text-slate-200">{report.industryOrigin.primary}</strong> ({report.industryOrigin.confidence} confidence, {report.industryOrigin.confidencePct}% match score).</p>
                      <p>The BOD:COD ratio of <strong className="text-slate-200">{report.biodegradability.bodCodRatio.toFixed(3)}</strong> indicates <strong className="text-slate-200">{report.biodegradability.label.toLowerCase()}</strong> organic matter, with an estimated biological treatment suitability of <strong className="text-slate-200">{report.biodegradability.biologicalTreatmentSuitability}%</strong>.</p>
                      {report.toxicityScreen.length > 0 && (
                        <p><strong className="text-red-400">{report.toxicityScreen.length} inhibitory compound(s)</strong> detected above biological inhibition thresholds ({report.toxicityScreen.map(t => t.label).join(', ')}). Pre-treatment is required before biological processes.</p>
                      )}
                      <p>Overall treatability score: <strong style={{ color: report.treatabilityColor }}>{report.overallTreatabilityScore}/100 — {report.treatabilityRating.charAt(0).toUpperCase() + report.treatabilityRating.slice(1)}</strong>.</p>
                    </div>
                  </div>

                  {/* Key Findings */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-200 mb-3">Key Findings</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {report.keyFindings.map((f, i) => (
                        <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-rb-elevated border border-slate-800/40">
                          <Circle className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" style={{ fill: '#3b82f6' }} />
                          <span className="text-xs text-slate-400">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommended Train Summary */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-200 mb-3">Recommended Treatment Sequence ({report.recommendedTrain.length} stages)</h3>
                    <div className="flex flex-wrap gap-2 items-center">
                      {report.recommendedTrain.map((step, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: `${STAGE_COLOR[step.stage]}18`, color: STAGE_COLOR[step.stage], border: `1px solid ${STAGE_COLOR[step.stage]}30` }}>
                            <span className="w-4 h-4 rounded-full text-[10px] flex items-center justify-center bg-black/20">{step.order}</span>
                            {step.equipment.split(' ')[0]}
                          </div>
                          {i < report.recommendedTrain.length - 1 && <ArrowRight className="w-3 h-3 text-slate-700" />}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Warnings */}
                  {report.warnings.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-red-400 mb-3">⚠ Critical Warnings</h3>
                      {report.warnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 rounded-xl bg-red-500/5 border border-red-800/30 mb-2">
                          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                          <p className="text-xs text-red-300">{w}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
